import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendFormDataRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { UpdateEventSettingsDto } from '@/types/dtos/events/request/update-event-settings.dto.ts';
import { GET_EVENT_SETTINGS_QUERY_KEY } from '@/state/queries/events/use-get-event-settings';
import { GET_EVENT_QUERY_KEY } from '@/state/queries/events/use-get-event.ts';

export const useUpdateEventSettingsMutation = (options?: { onSuccess?: () => void }) => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: UpdateEventSettingsDto) => {
      const token = await getToken();
      return updateEventSettings(dto, token ?? '');
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_SETTINGS_QUERY_KEY, variables.eventId] }),
      ]);
      options?.onSuccess?.();
    },
  });
};

/** Pure FormData builder — exported for unit tests. */
export function buildUpdateEventSettingsFormData(dto: UpdateEventSettingsDto): FormData {
  const formData = new FormData();
  if (dto.backgroundType) formData.append('backgroundType', dto.backgroundType);
  if (dto.backgroundColor) formData.append('backgroundColor', dto.backgroundColor);
  if (dto.textColor) formData.append('textColor', dto.textColor);
  if (dto.backgroundImage) formData.append('backgroundImage', dto.backgroundImage);
  if (dto.eventLogoImage) formData.append('eventLogoImage', dto.eventLogoImage);
  // Never send delete + upload together; upload wins on the server when both are present.
  if (dto.deleteEventLogo === true && !dto.eventLogoImage) {
    formData.append('deleteEventLogo', 'true');
  }
  if (dto.remoteSpeakerBackgroundImage) {
    formData.append('remoteSpeakerBackgroundImage', dto.remoteSpeakerBackgroundImage);
  } else if (dto.deleteRemoteSpeakerBackground === true) {
    formData.append('deleteRemoteSpeakerBackground', 'true');
  }
  if (dto.backgroundOverlay !== undefined) formData.append('backgroundOverlay', dto.backgroundOverlay.toString());
  if (dto.backgroundOverlayColor) formData.append('backgroundOverlayColor', dto.backgroundOverlayColor);
  if (dto.backgroundOverlayTransparency !== undefined)
    formData.append('backgroundOverlayTransparency', dto.backgroundOverlayTransparency.toString());
  if (dto.fontSizeMultiplier !== undefined) formData.append('fontSizeMultiplier', dto.fontSizeMultiplier.toString());
  if (dto.liveTranslationFontSizeMultiplier !== undefined)
    formData.append('liveTranslationFontSizeMultiplier', dto.liveTranslationFontSizeMultiplier.toString());
  if (dto.liveTranslationTextColor) formData.append('liveTranslationTextColor', dto.liveTranslationTextColor);
  if (dto.margins) {
    if (dto.margins.top !== undefined) formData.append('margins[top]', dto.margins.top.toString());
    if (dto.margins.bottom !== undefined) formData.append('margins[bottom]', dto.margins.bottom.toString());
    if (dto.margins.left !== undefined) formData.append('margins[left]', dto.margins.left.toString());
    if (dto.margins.right !== undefined) formData.append('margins[right]', dto.margins.right.toString());
  }

  if (dto.sponsors && dto.sponsors.length > 0) {
    dto.sponsors.forEach((sponsor, index) => {
      if (sponsor.id) formData.append(`sponsors[${index}][id]`, sponsor.id);
      if (sponsor.name) formData.append(`sponsors[${index}][name]`, sponsor.name);
      if (sponsor.image) formData.append(`sponsors[${index}][image]`, sponsor.image);
      if (sponsor.delete) formData.append(`sponsors[${index}][delete]`, 'true');
    });
  }

  return formData;
}

const updateEventSettings = async (dto: UpdateEventSettingsDto, token: string): Promise<void> => {
  const url = `${import.meta.env.VITE_API_URL}/events/${dto.eventId}/settings`;
  const formData = buildUpdateEventSettingsFormData(dto);
  await sendFormDataRequest({ url, method: METHOD.POST, body: formData, token });
};
